import CloseableModal from "@/components/CloseableModal"
import { createBasicSelect, IconButton } from "@/components/form"
import { type ObjectSchemaProperty } from "@/models"
import { faMagicWandSparkles, faPlusCircle, faSave, faTrashAlt, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed, reactive, ref, toRaw } from "vue"
import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import { getMarriageClaimProperties } from "../Helpers"
import ColumnManagerPresetModal from "./ColumnManagerPresetModal"
import ColumnManagerTable from "./ColumnManagerTable"

interface Props {
	columns: ObjectSchemaProperty[]
	onCancelled(): void
	onSave(columns: ObjectSchemaProperty[]): void
}

const gridStyles = css`
	display: grid;
	grid-template-columns: 1fr 4rem 4rem;
	gap: 0.5rem;
`

const MarriageKeySelect = createBasicSelect<ObjectSchemaProperty>()

const ColumnManagerModal: ReactiveComponent<Props> = (props) => {
	const property = ref<ObjectSchemaProperty | null>(null)
	const addingColumn = ref<boolean>(false)
	const columns = reactive<ObjectSchemaProperty[]>([...props.columns])
	const presetModalOpen = ref<boolean>(false)

	const filteredMarriageClaimProperties = computed(() =>
		getMarriageClaimProperties().filter((property) => !columns.includes(property))
	)

	function addColumn(column: ObjectSchemaProperty) {
		if (!columns.includes(column)) {
			columns.push(column)
			addingColumn.value = false
		}
	}

	function handleChangeColumnOrder(index1: number, index2: number) {
		const column = columns[index1]
		columns[index1] = columns[index2]
		columns[index2] = column
	}

	function handleRemoveColumn(removalIndex: number) {
		columns.splice(removalIndex, 1)
	}

	function handlePresetSave(newColumns: ObjectSchemaProperty[], replacement: boolean) {
		presetModalOpen.value = false
		if (replacement === true) {
			columns.splice(0, columns.length)
		}
		newColumns.forEach((column) => addColumn(column))
	}

	return () => (
		<CloseableModal title={`Column Manager`} onClose={props.onCancelled} style={{ width: "48rem", height: "40rem" }}>
			<div class="d-flex justify-content-between">
				<IconButton
					icon={faMagicWandSparkles}
					color="manage"
					type="button"
					onClick={() => (presetModalOpen.value = true)}
				>
					Choose Preset
				</IconButton>
				<IconButton icon={faTrashAlt} color="danger" type="button" onClick={() => columns.splice(0, columns.length)}>
					Delete all Columns
				</IconButton>
			</div>
			<hr />
			<div class="flex-grow-1 mb-2" style="overflow: auto;">
				<ColumnManagerTable
					columns={columns}
					changeColumnOrder={handleChangeColumnOrder}
					removeColumn={handleRemoveColumn}
				/>
			</div>
			{addingColumn.value ? (
				<div class={gridStyles}>
					<MarriageKeySelect
						options={filteredMarriageClaimProperties.value}
						value={property.value}
						setValue={(newProperty) => (property.value = newProperty)}
						getText={(property) => property.formatName()}
						getValue={(property) => property.vueKey()}
						defaultText="Please select a field"
						style="width:100%"
						required
					/>
					<IconButton
						icon={faPlusCircle}
						type="button"
						color="success"
						onClick={() => {
							addColumn(property.value!)
							property.value = null
						}}
						disabled={!property.value}
					/>
					<IconButton
						icon={faXmarkCircle}
						color="danger"
						onClick={() => {
							addingColumn.value = false
							property.value = null
						}}
					/>
				</div>
			) : (
				<IconButton
					style="width: 100%"
					icon={faPlusCircle}
					color={"success"}
					type="button"
					onClick={() => (addingColumn.value = true)}
				>
					Add New Column
				</IconButton>
			)}
			<IconButton
				class="mt-2"
				icon={faSave}
				color={"primary"}
				type="button"
				onClick={() => props.onSave(toRaw(columns))}
				disabled={addingColumn.value || columns.length === 0}
			/>
			{presetModalOpen.value && (
				<ColumnManagerPresetModal onCancelled={() => (presetModalOpen.value = false)} onSave={handlePresetSave} />
			)}
		</CloseableModal>
	)
}
export default defineComponent(ColumnManagerModal, inferProps<Props>())
